import { CirclePlay } from "lucide-react";
import { useEffect, useMemo, type FC } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { EmptyState } from "@/components/ui/empty-state";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";

import {
  courseSections,
  TUTORIALS,
  tutorialsFor,
  UPCOMING_TUTORIALS,
  upcomingFor
} from "../catalog";
import { CourseSidebar } from "../components/CourseSidebar";
import { TutorialLesson } from "../components/TutorialLesson";
import { TUTORIALS_PATH } from "../routes";
import { clearTutorialsLater } from "../seen";
import type { Tutorial, UpcomingTutorial } from "../types";

type Props = {
  /** Default to the shipped and upcoming catalogs. */
  catalog?: Tutorial[];
  upcomingCatalog?: UpcomingTutorial[];
};

/**
 * Tutorials as a course: topics and their tutorials down the side, with
 * what's coming, and the open tutorial as a lesson. `/tutorials` opens the
 * newest; `/tutorials/:tutorialId?t=` opens one, that many seconds in.
 */
const TutorialsPage: FC<Props> = ({
  catalog = TUTORIALS,
  upcomingCatalog = UPCOMING_TUTORIALS
}) => {
  const {
    userDetails: { id: uid, userType }
  } = useGetUserDetails();
  const { tutorialId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tutorials = useMemo(
    () => tutorialsFor(userType, catalog),
    [userType, catalog]
  );
  const upcoming = useMemo(
    () => upcomingFor(userType, tutorials, upcomingCatalog),
    [userType, tutorials, upcomingCatalog]
  );
  const sections = courseSections(tutorials, upcoming);
  const course = sections.flatMap((s) => s.tutorials);
  const linked = tutorials.find((t) => t.id === tutorialId);
  const current = linked ?? tutorials[0];

  useEffect(() => {
    if (uid) clearTutorialsLater(uid);
  }, [uid]);

  useEffect(() => {
    if (tutorialId && !linked) navigate(TUTORIALS_PATH, { replace: true });
  }, [tutorialId, linked, navigate]);

  if (!current) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center bg-surface">
        <EmptyState
          icon={CirclePlay}
          title="No Tutorials Yet"
          description="New videos show up here when they're released."
        />
      </div>
    );
  }

  const open = (id: string) => navigate(`${TUTORIALS_PATH}/${id}`);

  return (
    <div className="flex h-full min-h-0 w-full bg-surface">
      <CourseSidebar
        sections={sections}
        activeId={current.id}
        onSelect={open}
      />
      <div key={current.id} className="min-w-0 flex-1 overflow-y-auto">
        <TutorialLesson
          tutorial={current}
          initialTime={linked ? Number(params.get("t")) || 0 : 0}
          next={course[course.indexOf(current) + 1]}
          upcoming={upcoming}
          onSelect={open}
        />
      </div>
    </div>
  );
};

export default TutorialsPage;
